import {SelectionModel} from "@angular/cdk/collections";
import {TitleCasePipe} from "@angular/common";
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MatButtonToggleModule} from "@angular/material/button-toggle";
import {MatDivider} from "@angular/material/divider";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIcon} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {AgreementType, LeasePricingPeriod, LeaseSummary, OccupancyType} from "@features/leasing/leasing.model";
import {AppCoreModule} from "@modules/app.core.module";
import {PanelType} from "@shared-components/info-panel/info-panel.component";
import {DateUtilsService} from "../../../../../shared/services/dateUtils.service";
import {UtilsService} from "../../../../../shared/services/utils.service";
import {
  BookingConfig,
  BookingEstimation,
  GridSpacePricingTimePeriod,
  perPricingPeriod,
  RateType,
  Space,
  SpaceDefaultRate,
  SpacePricingRateCategory
} from "../../bookings.model";
import {LeaseBookingsService} from "../../lease-bookings.service";

@Component({
  selector: 'app-booking-rates-and-charges',
  standalone: true,
  imports: [
    FlexModule,
    MatDivider,
    MatIcon,
    TitleCasePipe,
    MatSlideToggleModule,
    AppCoreModule,
    MatButtonToggleModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
  ],
  templateUrl: './booking-rates-and-charges.component.html',
  styleUrl: './booking-rates-and-charges.component.scss'
})
export class BookingRatesAndChargesComponent implements OnInit {
  @Input() bookingConfig: BookingConfig = new BookingConfig();
  @Input() spaces: Space[] = []
  @Input() retailerId?: number;

  @Input() editType: 'new' | 'add' | 'edit' = 'new';
  @Input() lease: LeaseSummary | null = null;
  @Input() pricingPeriod: LeasePricingPeriod | null = null;

  @Output() previous = new EventEmitter<void>();
  @Output() next = new EventEmitter<void>();

  selectedRateIds = new SelectionModel<string>(true, []);

  allowCombineRates = true;
  combinedFixedCategory: SpacePricingRateCategory | null = null;
  combinedVariableCategory: SpacePricingRateCategory | null = null;
  periodType: GridSpacePricingTimePeriod | null = null;

  addingCustom = false;
  customRateForm!: FormGroup;
  protected readonly PanelType = PanelType;
  protected readonly perPricingPeriod = perPricingPeriod;
  protected readonly SpacePricingRateCategory = SpacePricingRateCategory;

  constructor(private bookingsService: LeaseBookingsService,
              private dateUtils: DateUtilsService,
              public utils: UtilsService) {
    this.customRateForm = new FormGroup({
      fixedCategory: new FormControl<SpacePricingRateCategory>(SpacePricingRateCategory.PER_PERIOD, Validators.required),
      variableCategory: new FormControl<SpacePricingRateCategory>(SpacePricingRateCategory.PERCENTAGE, Validators.required),
      fixedRate: new FormControl<number>(0, Validators.required),
      variableRate: new FormControl<number>(0, Validators.required)
    })
  }

  ngOnInit() {
    if (this.editType == 'edit' && this.lease && this.pricingPeriod) {
      this.generateDefaultPricing();
    }

    if (this.bookingConfig.bookingRates.length > 0) {
      this.setExistingPricing();
    } else {
      this.bookingConfig.spaceIds.forEach(ea => this.selectSpaceRate(ea));
    }

    this.setTimePeriod();
    this.allowCombineRates = this.combineRates();
    this.setBookingEstimation();

    this.selectedRateIds.changed.subscribe(change => {
      this.allowCombineRates = this.combineRates();
      this.setBookingEstimation();
    })

    this.customRateForm.valueChanges.subscribe(change => {
      this.setBookingEstimation();
    })
  }

  get bookingEstimation() {
    return this.bookingConfig.bookingEstimation;
  }

  get leasePeriodDays(): number {
    const start = new Date(this.bookingConfig.selectedDates[0]);
    const end = new Date(this.bookingConfig.selectedDates[this.bookingConfig.selectedDates.length - 1]);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  }

  get leasePeriods(): number {
    switch (this.periodType) {
      case GridSpacePricingTimePeriod.DAILY:
        return this.bookingConfig.selectedDates.length;
      case GridSpacePricingTimePeriod.WEEKLY:
        return this.dateUtils.getNumberOfPeriods(this.bookingConfig.startDate, this.bookingConfig.endDate, 'week');
      case GridSpacePricingTimePeriod.WEEKEND:
        return this.dateUtils.getNumberOfWeekends(this.bookingConfig.startDate, this.bookingConfig.endDate, this.bookingConfig.includeDays.includes('Friday'));
      case GridSpacePricingTimePeriod.MONTHLY:
        return this.dateUtils.getNumberOfPeriods(this.bookingConfig.startDate, this.bookingConfig.endDate, 'month');
    }

    return 0;
  }

  get selectedSpaces(): Space[] {
    return this.spaces.filter(f => this.bookingConfig.spaceIds.includes(f.id));
  }

  get spaceNames(): string {
    return this.selectedSpaces
      .map(m => m.spaceName)
      .join(', ');
  }

  get spaceSize(): number {
    return this.selectedSpaces
      .map(m => m.spaceSize)
      .reduce((acc, size) => acc + size, 0);
  }

  get combinedTypeUnit(): string | null {
    if (this.periodType) {
      return perPricingPeriod[this.periodType].period;
    }
    return null;
  }

  get combinedPeriodRate(): number {
    if (this.allowCombineRates) {
      const fixedPricingCategory = this.selectedRateSpaces
        .map(m => this.bookingsService.getDefaultPricing(m, this.bookingConfig.patternType))
        .filter(f => !!f)
        .map(sdr => {
          const fixedDefault = sdr!.filter(f => f.rateType == RateType.FIXED && f.category == this.combinedFixedCategory!);
          if (fixedDefault.length > 0) return fixedDefault[0].value
          return 0;
        })

      if (this.combinedFixedCategory == SpacePricingRateCategory.PER_PERIOD) return fixedPricingCategory.reduce((acc, val) => acc + val, 0);
      if (this.combinedFixedCategory == SpacePricingRateCategory.PER_SQM) return fixedPricingCategory.sort((a, b) => b - a)[0];
      return 0;
    }
    return 0;
  }

  get combinedVariableRate(): number {
    if (this.allowCombineRates) {
      const fixedPricingCategory = this.selectedRateSpaces
        .map(m => this.bookingsService.getDefaultPricing(m, this.bookingConfig.patternType))
        .filter(f => !!f)
        .map(sdr => {
          const variableDefault = sdr!.filter(f => f.rateType == RateType.VARIABLE && f.category == this.combinedVariableCategory!);
          if (variableDefault.length > 0) return variableDefault[0].value
          return 0;
        })
      if (this.combinedVariableCategory == SpacePricingRateCategory.PERCENTAGE) return fixedPricingCategory.sort((a, b) => b - a)[0];
      return 0;
    }
    return 0;
  }

  get combinedEstimatedCost(): number {
    if (this.allowCombineRates) {
      if (this.combinedFixedCategory == SpacePricingRateCategory.PER_SQM) {
        return this.leasePeriods * this.effectiveTotalSpace * this.combinedPeriodRate;
      }
      if (this.combinedFixedCategory == SpacePricingRateCategory.PER_PERIOD) {
        return this.leasePeriods * this.combinedPeriodRate;
      }
    }
    return 0;
  }

  get combinedPercentageCost(): string {
    if (this.allowCombineRates) {
      if (this.combinedVariableCategory == SpacePricingRateCategory.PERCENTAGE) {
        return '+ ' + this.combinedVariableRate + '% turnover';
      }
    }
    return '';
  }

  get combinedSpaceNames(): string {
    return this.selectedRateSpaces
      .map(m => m.spaceName)
      .join(', ');
  }

  get selectedRateSpaces(): Space[] {
    return this.selectedSpaces.filter(f => this.selectedRateIds.isSelected(f.id.toString()));
  }

  get customRentPerPeriod(): number {
    if (this.customRateForm.get('fixedCategory')!.value == SpacePricingRateCategory.PER_SQM) {
      return this.effectiveTotalSpace * this.customRateForm.get('fixedRate')?.value;
    }
    if (this.customRateForm.get('fixedCategory')!.value == SpacePricingRateCategory.PER_PERIOD) {
      return this.customRateForm.get('fixedRate')?.value;
    }
    return 0;
  }

  get customEstimatedCost(): number {
    if (this.customRateForm.get('fixedCategory')!.value == SpacePricingRateCategory.PER_SQM) {
      return this.leasePeriods * this.effectiveTotalSpace * this.customRateForm.get('fixedRate')?.value;
    }
    if (this.customRateForm.get('fixedCategory')!.value == SpacePricingRateCategory.PER_PERIOD) {
      return this.leasePeriods * this.customRateForm.get('fixedRate')?.value;
    }
    return 0;
  }

  get customPercentageCost(): string {
    const variableRate = this.customRateForm.get('variableRate')?.value;
    if (variableRate > 0) return '+ ' + variableRate + '% turnover';
    return '';
  }

  get customSelected() {
    return this.selectedRateIds.isSelected('custom');
  }

  get effectiveTotalSpace(): number {
    if (this.customSelected) {
      return this.selectedSpaces.map(m => m.spaceSize)
        .reduce((acc, size) => acc + size, 0);
    } else {
      return this.selectedRateSpaces.map(m => m.spaceSize)
        .reduce((acc, size) => acc + size, 0);
    }
  }

  get canSubmit(): boolean {
    return this.selectedRateIds.hasValue()
      && this.bookingEstimation != null
      && (this.allowCombineRates || this.customSelected)
      && (!this.customSelected || this.customRateForm.valid);
  }

  setExistingPricing() {
    new Set(this.bookingConfig.bookingRates.map(m => m.spaceId))
      .forEach(ea => this.selectSpaceRate(ea.toString()));
    this.addingCustom = this.bookingConfig.customRates;
    if (this.addingCustom) {
      this.selectSpaceRate('custom');
      if (this.bookingConfig.bookingEstimation) {
        this.customRateForm.patchValue({
          fixedCategory: this.bookingConfig.bookingEstimation?.fixedCategoryType,
          variableCategory: this.bookingConfig.bookingEstimation?.variableCategoryType,
          fixedRate: this.bookingConfig.bookingEstimation?.fixedRate,
          variableRate: this.bookingConfig.bookingEstimation?.variableRate,
        })
      }
    }
  }

  generateDefaultPricing() {
    if (!this.pricingPeriod?.bookingPricing) {
      this.addingCustom = true;
    } else {
      const selectedPricing = this.pricingPeriod!.bookingPricing.bookingRates
        .map(m => m.id.toString());
      this.selectedRateIds.select(...selectedPricing);
    }
  }

  setBookingEstimation() {
    if (this.selectedRateIds.hasValue()) {
      if (this.customSelected) {
        const fixedCat: SpacePricingRateCategory = this.customRateForm.get('fixedCategory')!.value;
        const fixedRate: number = this.customRateForm.get('fixedRate')!.value;
        const bookingEstimation: BookingEstimation = {
          effectiveSpaces: 'Custom',
          totalSpace: this.effectiveTotalSpace,
          periodType: this.periodType!,
          periodTypeUnit: this.combinedTypeUnit!,
          leasePeriodDays: this.leasePeriodDays,
          leasePeriods: this.leasePeriods,
          fixedRate: this.customRateForm.get('fixedRate')!.value,
          variableRate: this.customRateForm.get('variableRate')!.value,
          fixedCategoryType: this.customRateForm.get('fixedCategory')!.value,
          variableCategoryType: this.customRateForm.get('variableCategory')!.value,
          fixedCost: this.customEstimatedCost,
          variableCost: this.customPercentageCost,
          rentPerPeriod: fixedCat == SpacePricingRateCategory.PER_SQM ? fixedRate * this.effectiveTotalSpace : fixedRate
        }

        this.bookingConfig.bookingEstimation = {...bookingEstimation};
      } else {
        const bookingEstimation: BookingEstimation = {
          effectiveSpaces: this.combinedSpaceNames,
          totalSpace: this.effectiveTotalSpace,
          periodType: this.periodType!,
          periodTypeUnit: this.combinedTypeUnit!,
          leasePeriodDays: this.leasePeriodDays,
          leasePeriods: this.leasePeriods,
          fixedRate: this.combinedPeriodRate,
          variableRate: this.combinedVariableRate,
          fixedCategoryType: this.combinedFixedCategory!,
          variableCategoryType: this.combinedVariableCategory!,
          fixedCost: this.combinedEstimatedCost,
          variableCost: this.combinedPercentageCost,
          rentPerPeriod: this.combinedFixedCategory == SpacePricingRateCategory.PER_SQM ? this.combinedPeriodRate * this.effectiveTotalSpace : this.combinedPeriodRate
        }

        this.bookingConfig.bookingEstimation = {...bookingEstimation};
      }
    }
  }

  setTimePeriod() {
    const period = Object.values(GridSpacePricingTimePeriod)
      .find(key => perPricingPeriod[key].patternType.includes(this.bookingConfig.patternType));
    if (period) this.periodType = period;
  }

  selectSpaceRate(spaceId: string) {
    if (spaceId == 'custom') {
      if (this.selectedRateIds.isSelected('custom')) {
        this.selectedRateIds.toggle('custom');
      } else {
        this.selectedRateIds.clear();
        this.selectedRateIds.select('custom');
      }
    } else {
      this.selectedRateIds.deselect('custom')
      this.selectedRateIds.toggle(spaceId.toString());
    }
  }

  hasDefaultPricing(spaceId: string): boolean {
    const defaultPricing = this.bookingsService.getDefaultPricing(this.space(spaceId)!, this.bookingConfig.patternType);
    return defaultPricing != null && defaultPricing.length > 0;
  }

  periodTypeRate(spaceId: string): string {
    const defaultPricing = this.bookingsService.getDefaultPricing(this.space(spaceId)!, this.bookingConfig.patternType);

    if (defaultPricing && defaultPricing.length > 0) {
      return defaultPricing[0].timePeriod;
    }

    return 'No default pricing';
  }

  periodTypeUnit(spaceId: string): string {
    const defaultPricing = this.bookingsService.getDefaultPricing(this.space(spaceId)!, this.bookingConfig.patternType);

    if (defaultPricing && defaultPricing.length > 0) {
      return perPricingPeriod[defaultPricing[0].timePeriod].period;
    }

    return 'No default pricing';
  }

  fixedRateType(spaceId: string): SpacePricingRateCategory | null {
    const defaultPricing = this.bookingsService.getDefaultPricing(this.space(spaceId)!, this.bookingConfig.patternType);
    if (defaultPricing && defaultPricing.length > 0) {
      const fixedDefault = defaultPricing.filter(f => f.rateType == RateType.FIXED);
      if (fixedDefault.length > 0) return fixedDefault[0].category;
    }
    return null;
  }

  rentPerPeriod(spaceId: string): number {
    const defaultPricing = this.bookingsService.getDefaultPricing(this.space(spaceId)!, this.bookingConfig.patternType);

    if (defaultPricing && defaultPricing.length > 0) {
      const fixedDefault = defaultPricing.filter(f => f.rateType == RateType.FIXED);
      if (fixedDefault.length > 0) {
        if (fixedDefault[0].category == SpacePricingRateCategory.PER_SQM) {
          return this.space(spaceId)!.spaceSize * fixedDefault[0].value;
        }
        if (fixedDefault[0].category == SpacePricingRateCategory.PER_PERIOD) {
          return fixedDefault[0].value;
        }
      }
    }
    return 0;
  }

  estimatedCost(spaceId: string): number {
    const defaultPricing = this.bookingsService.getDefaultPricing(this.space(spaceId)!, this.bookingConfig.patternType);

    if (defaultPricing && defaultPricing.length > 0) {
      const fixedDefault = defaultPricing.filter(f => f.rateType == RateType.FIXED);
      if (fixedDefault.length > 0) {
        if (fixedDefault[0].category == SpacePricingRateCategory.PER_SQM) {
          return this.leasePeriods * this.space(spaceId)!.spaceSize * fixedDefault[0].value;
        }
        if (fixedDefault[0].category == SpacePricingRateCategory.PER_PERIOD) {
          return this.leasePeriods * fixedDefault[0].value;
        }
      }
    }
    return 0;
  }

  percentageCost(spaceId: string): string {
    const defaultPricing = this.bookingsService.getDefaultPricing(this.space(spaceId)!, this.bookingConfig.patternType);

    if (defaultPricing && defaultPricing.length > 0) {
      const percentage = defaultPricing.filter(f => f.rateType == RateType.VARIABLE);
      if (percentage.length > 0) {
        return '+ ' + percentage[0].value + '% turnover';
      }
    }
    return '';
  }

  periodRate(spaceId: string): number {
    const defaultPricing = this.bookingsService.getDefaultPricing(this.space(spaceId)!, this.bookingConfig.patternType);
    if (defaultPricing && defaultPricing.length > 0) {
      if (defaultPricing[0].rateType == RateType.FIXED) return defaultPricing[0].value;
    }
    return 0;
  }

  addCustomRate() {
    this.addingCustom = true;
    this.selectSpaceRate('custom');
  }

  combineRates(): boolean {
    this.combinedFixedCategory = null;
    this.combinedVariableCategory = null;

    let blankPricingSelected = false;

    // find all fixed rate categories
    this.selectedRateSpaces.forEach(ea => {
      const defaultPricing = this.bookingsService.getDefaultPricing(ea, this.bookingConfig.patternType);
      blankPricingSelected = blankPricingSelected || (!defaultPricing || defaultPricing.length == 0);
    })

    if (blankPricingSelected) return false;

    const fixedCategories = this.selectedRateSpaces
      .map(m => this.bookingsService.getDefaultPricing(m, this.bookingConfig.patternType))
      .filter(f => !!f)
      .map(sdr => {
        const fixedDefault = sdr!.filter(f => f.rateType == RateType.FIXED);
        if (fixedDefault.length > 0) return fixedDefault[0].category;
        return null;
      })
      .filter(f => f != null);

    // find all fixed variable categories
    const variableCategories = this.selectedRateSpaces
      .map(m => this.bookingsService.getDefaultPricing(m, this.bookingConfig.patternType))
      .filter(f => !!f)
      .map(sdr => {
        const fixedDefault = sdr!.filter(f => f.rateType == RateType.VARIABLE);
        if (fixedDefault.length > 0) return fixedDefault[0].category;
        return null;
      })
      .filter(f => f != null);

    const fixedSet = new Set(fixedCategories);
    const variableSet = new Set(variableCategories);
    // set these categories if only one
    if (fixedSet.size == 1) this.combinedFixedCategory = fixedCategories[0];
    if (variableSet.size == 1) this.combinedVariableCategory = variableCategories[0];

    return (fixedSet.size <= 1 && variableSet.size <= 1)
      && (fixedSet.size == 1 || variableSet.size == 1);
  }

  space(spaceId: string) {
    return this.spaces.find(f => f.id == spaceId);
  }

  prevStep() {
    this.previous.emit();
  }

  nextStep() {
    if (this.bookingEstimation) {
      if (this.customSelected) {
        const fixedRate: SpaceDefaultRate = {
          id: 0,
          spaceId: 0,
          rateType: RateType.FIXED,
          category: this.bookingEstimation.fixedCategoryType,
          timePeriod: this.bookingEstimation.periodType,
          value: this.bookingEstimation.fixedRate,
          isDefault: true,
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
        }
        const variableRate: SpaceDefaultRate = {
          id: 0,
          spaceId: 0,
          rateType: RateType.VARIABLE,
          category: this.bookingEstimation.variableCategoryType,
          timePeriod: this.bookingEstimation.periodType,
          value: this.bookingEstimation.variableRate,
          isDefault: true,
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
        }
        this.bookingConfig.bookingRates = [fixedRate, variableRate];
        this.bookingConfig.spacePricingRateCategory = fixedRate.category;
        this.bookingConfig.customRates = true;
      }

      if (!this.customSelected) {
        const rates = this.selectedRateSpaces.flatMap(m => {
          return this.bookingsService.getDefaultPricing(m, this.bookingConfig.patternType)!
        });
        this.bookingConfig.bookingRates = [...rates];
        this.bookingConfig.spacePricingRateCategory = this.combinedFixedCategory;
        this.bookingConfig.customRates = false;
      }
    }
    this.next.emit();
  }
}
